import React, { useState } from "react";
import { DemoModal } from "@/components/demos/DemoModal";
import { GameBoard } from "./GameBoard";
import DemoShell from "@/components/demos/DemoShell";
import { Button } from "@/components/ui/button";
import {
  TicTacToeModelSelector,
  ModelType,
  getStoredModel,
} from "./TicTacToeModelSelector";
import { Winner } from "./gameUtils";

export const TicTacToeDemo = () => {
  const [xModel, setXModel] = useState<ModelType>(() =>
    getStoredModel("X Player")
  );
  const [oModel, setOModel] = useState<ModelType>(() =>
    getStoredModel("O Player")
  );
  const [showModal, setShowModal] = useState(false);
  const [key, setKey] = useState(0);
  const [stats, setStats] = useState({ xWins: 0, oWins: 0, draws: 0 });

  // Track changes to models
  const [tempXModel, setTempXModel] = useState<ModelType>(xModel);
  const [tempOModel, setTempOModel] = useState<ModelType>(oModel);
  const [hasChanges, setHasChanges] = useState(false);

  const handleReset = () => {
    setKey((prev) => prev + 1);
    setStats({ xWins: 0, oWins: 0, draws: 0 });
  };

  const handleGameEnd = (winner: Winner) => {
    setStats((prev) => {
      if (winner === "draw") {
        return { ...prev, draws: prev.draws + 1 };
      } else if (winner === "x") {
        return { ...prev, xWins: prev.xWins + 1 };
      } else {
        return { ...prev, oWins: prev.oWins + 1 };
      }
    });
  };

  const handleModelChange = (player: "x" | "o", model: ModelType) => {
    if (player === "x") {
      setTempXModel(model);
    } else {
      setTempOModel(model);
    }
    setHasChanges(true);
  };

  const handleSave = () => {
    setXModel(tempXModel);
    setOModel(tempOModel);
    setHasChanges(false);
    setKey((prev) => prev + 1); // Reset the game with new models
  };

  if (showModal) {
    return (
      <DemoModal onExit={() => setShowModal(false)} onReset={handleReset}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b">
            <div className="max-w-md mx-auto space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Game Settings</h2>
                <Button
                  onClick={handleSave}
                  variant={hasChanges ? "default" : "outline"}
                  className={
                    hasChanges ? "bg-orange-500 hover:bg-orange-600" : ""
                  }
                >
                  Apply Changes
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TicTacToeModelSelector
                  label="X Player"
                  value={tempXModel}
                  onChange={(model) => handleModelChange("x", model)}
                />
                <TicTacToeModelSelector
                  label="O Player"
                  value={tempOModel}
                  onChange={(model) => handleModelChange("o", model)}
                />
              </div>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <GameBoard
              key={key}
              resetKey={key}
              xModel={xModel}
              oModel={oModel}
              onGameEnd={handleGameEnd}
              stats={stats}
            />
          </div>
        </div>
      </DemoModal>
    );
  }

  return (
    <DemoShell title="Tic Tac Toe" description="Play Tic Tac Toe with an LLM">
      <div className="max-w-md mx-auto p-6 space-y-8 text-center">
        <h1 className="text-2xl font-bold">Choose Players</h1>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <TicTacToeModelSelector
              label="X Player"
              value={xModel}
              onChange={setXModel}
            />
            <TicTacToeModelSelector
              label="O Player"
              value={oModel}
              onChange={setOModel}
            />
          </div>
        </div>
        <Button
          size="lg"
          onClick={() => {
            setTempXModel(xModel);
            setTempOModel(oModel);
            setHasChanges(false);
            setShowModal(true);
          }}
          className="min-w-[200px]"
        >
          Start Game
        </Button>
      </div>
    </DemoShell>
  );
};
